import { ReactNode } from "react";
import { Navigate } from "react-router-dom";

import AuthContainer from "~/components/containers/AuthContainer/AuthContainer";
import FullscreenLoader from "~/components/ui/loaders/FullscreenLoader";
import { useTeam } from "~/contexts/TeamProvider";
import { routes } from "~/router";
import { HubspotTracking } from "~/tracking/hubspot/HubspotTracking";

import OnboardingStepExits from "./OnboardingStepExits";
import {
  OnboardingStep,
  getRequiredOnboardingStep,
  onboardingStepPath,
} from "./onboardingStep";

type Props = {
  step: OnboardingStep;
  children: ReactNode;
};

/**
 * Shows an onboarding step full-screen (no sidebar: only switching the team
 * or signing out leaves it) while the backend requires it. Once the team completes it (or never needed it),
 * sends the user on to the next required step or to the app.
 */
const OnboardingStepRoute = ({ step, children }: Props) => {
  const { teamId, team, status, isLoading, isFetching } = useTeam();

  if (!teamId) {
    return <Navigate to={routes.selectTeam()} replace />;
  }

  if (status === "pending" || isLoading || (isFetching && !team)) {
    return <FullscreenLoader />;
  }

  if (!team) {
    return <Navigate to={routes.selectTeam()} replace />;
  }

  const requiredStep = getRequiredOnboardingStep(team);

  if (requiredStep !== step) {
    return (
      <Navigate
        to={requiredStep ? onboardingStepPath(requiredStep) : routes.home()}
        replace
      />
    );
  }

  return (
    <AuthContainer>
      <HubspotTracking />
      {children}
      <OnboardingStepExits />
    </AuthContainer>
  );
};

export default OnboardingStepRoute;
